import React from 'react';
import { Box, Card, CardContent, Grid, Typography, alpha, useTheme } from '@mui/material';
import { KpiTileModel } from '../utils/tiles';
import { DeltaText } from './DeltaText';
import { Sparkline } from './Sparkline';

export interface KpiTileProps {
  tile: KpiTileModel;
}

/** One headline figure with its change and context, tinted by the tile's colour (same treatment as the nominations stats). */
export const KpiTile: React.FC<KpiTileProps> = ({ tile }) => {
  const theme = useTheme();
  const color = theme.palette[tile.color].main;

  return (
    <Card
      role="group"
      aria-label={tile.label}
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '16px',
        border: `1px solid ${alpha(color, 0.22)}`,
        background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {tile.label}
          </Typography>
          {tile.spark ? (
            <Sparkline counts={tile.spark.counts} latestFrom={tile.spark.latestFrom} height={36} aria-label={`${tile.label} trend`} />
          ) : null}
        </Box>
        <Typography component="div" sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
          {tile.value}
        </Typography>
        {tile.delta ? <DeltaText delta={tile.delta} suffix={tile.deltaSuffix} /> : null}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 'auto' }}>
          {tile.caption}
        </Typography>
      </CardContent>
    </Card>
  );
};

export interface KpiTilesProps {
  tiles: KpiTileModel[];
}

export const KpiTiles: React.FC<KpiTilesProps> = ({ tiles }) => (
  <Grid container spacing={2.5}>
    {tiles.map((tile) => (
      <Grid item xs={12} md={tiles.length > 3 ? 3 : 4} key={tile.key}>
        <KpiTile tile={tile} />
      </Grid>
    ))}
  </Grid>
);
